// Shared helpers + small presentational pieces for the translation-task screens.
import React from 'react';
import { Box, Typography } from '@mui/material';
import dayjs from 'dayjs';

// ---- status pills -----------------------------------------------------------
// Covers both task-level (active/completed/cancelled) and assignment-level
// (pending/submitted/approved/rejected) statuses.
export const STATUS_STYLES = {
  active: { label: 'Active', color: '#2563eb', bg: '#eff6ff' },
  completed: { label: 'Completed', color: '#059669', bg: '#ecfdf5' },
  cancelled: { label: 'Cancelled', color: '#64748b', bg: '#f1f5f9' },
  pending: { label: 'Pending', color: '#d97706', bg: '#fffbeb' },
  submitted: { label: 'Submitted', color: '#2563eb', bg: '#eff6ff' },
  approved: { label: 'Approved', color: '#059669', bg: '#ecfdf5' },
  rejected: { label: 'Rejected', color: '#dc2626', bg: '#fef2f2' },
};

export const StatusPill = ({ status, size = 'md' }) => {
  const key = String(status || '').toLowerCase();
  const style = STATUS_STYLES[key] || { label: status || '—', color: '#64748b', bg: '#f1f5f9' };
  const small = size === 'sm';
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: small ? 1 : 1.25,
        py: small ? 0.25 : 0.5,
        borderRadius: '999px',
        background: style.bg,
        color: style.color,
        fontSize: small ? 12 : 12.5,
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      <Box
        component="span"
        sx={{ width: 7, height: 7, borderRadius: '50%', background: style.color }}
      />
      {style.label}
    </Box>
  );
};

// ---- progress bar -----------------------------------------------------------
// approved / total, turns green once everything is approved.
export const ApprovalProgress = ({ approved = 0, total = 0, showLabel = true }) => {
  const pct = total > 0 ? Math.round((approved / total) * 100) : 0;
  const done = total > 0 && approved === total;
  return (
    <Box sx={{ minWidth: 120 }}>
      <Box
        sx={{
          height: 6,
          borderRadius: '999px',
          background: '#e2e8f0',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            width: `${pct}%`,
            height: '100%',
            borderRadius: '999px',
            background: done
              ? 'linear-gradient(90deg, #10b981 0%, #059669 100%)'
              : 'linear-gradient(90deg, #6366f1 0%, #4f46e5 100%)',
            transition: 'width 0.3s ease',
          }}
        />
      </Box>
      {showLabel && (
        <Typography sx={{ mt: 0.5, fontSize: 12, color: '#94a3b8' }}>
          {approved} / {total} approved
        </Typography>
      )}
    </Box>
  );
};

// ---- avatar helpers ---------------------------------------------------------
const AVATAR_COLORS = ['#6366f1', '#0ea5e9', '#10b981', '#ec4899', '#f59e0b', '#8b5cf6', '#ef4444'];

export const colorForName = (name = '') => {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

export const getInitials = (name = '') =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0] || '')
    .join('')
    .toUpperCase() || '?';

// ---- formatting -------------------------------------------------------------
export const formatBytes = (bytes) => {
  if (bytes == null || Number.isNaN(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
};

export const formatDate = (value) => (value ? dayjs(value).format('MMM D, YYYY') : '—');

// small colored badge for a file type (xls / pdf / img …)
export const fileTypeMeta = (fileType = '', fileName = '') => {
  const ext = (fileType || fileName.split('.').pop() || '').toLowerCase();
  if (['xlsx', 'xls', 'csv'].includes(ext)) return { label: 'XLS', color: '#15803d' };
  if (ext === 'pdf') return { label: 'PDF', color: '#dc2626' };
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext))
    return { label: 'IMG', color: '#7c3aed' };
  return { label: (ext || 'FILE').slice(0, 4).toUpperCase(), color: '#64748b' };
};

export const FileTypeBadge = ({ fileType, fileName, size = 44 }) => {
  const meta = fileTypeMeta(fileType, fileName);
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '10px',
        background: meta.color,
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 12,
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {meta.label}
    </Box>
  );
};

// Force a real download instead of opening the file in a new tab. The `download`
// attribute is ignored for cross-origin URLs (like S3 signed links), so we fetch
// the bytes and save a blob. If S3's CORS blocks the fetch, we fall back to the
// old open-in-new-tab behaviour so the link still works.
export const downloadFile = async (url, filename) => {
  if (!url) return;
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();
    const objectUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename || 'download';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(objectUrl);
  } catch (err) {
    // CORS or network issue — fall back to opening the signed URL directly
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.click();
  }
};

// pull a readable message out of an axios error, preferring the API envelope's `message`
export const parseTaskError = (err, fallback) => {
  const status = err?.response?.status;
  const message = err?.response?.data?.message;
  if (status === 401) {
    return {
      status,
      message: message || 'You are not authorized to manage tasks.',
      unauthorized: true,
    };
  }
  return { status, message: message || fallback, unauthorized: false };
};
